import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { LayoutVariant, LAYOUT_BREAKPOINTS } from '../types/layout';
import { createEventListenerCleanup, combineCleanups } from '../utils/effectCleanup';

const STREAMER_MODE_STORAGE_KEY = 'wallstreetle-streamer-mode';
const STREAMER_MODE_EVENT = 'wallstreetle-streamer-mode-change';

const readStreamerModePreference = () => {
  if (typeof window === 'undefined') return false;
  try {
    return localStorage.getItem(STREAMER_MODE_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
};

/**
 * Custom hook for detecting and managing layout variants based on viewport size
 * Automatically switches between mobile and desktop layouts at the configured breakpoint
 * Optimized to prevent unnecessary re-renders and object recreations
 */
export const useLayoutVariant = (): LayoutVariant => {
  // Track the current layout type only (not the full object)
  const [layoutType, setLayoutType] = useState<'mobile' | 'desktop'>(() => {
    if (typeof window === 'undefined') return 'mobile';
    return window.innerWidth >= LAYOUT_BREAKPOINTS.desktop ? 'desktop' : 'mobile';
  });

  const [isStreamerMode, setIsStreamerMode] = useState<boolean>(readStreamerModePreference);

  // Use ref to track the last known width to avoid unnecessary updates
  const lastWidthRef = useRef<number>(
    typeof window !== 'undefined' ? window.innerWidth : 0
  );

  const isActuallyDesktop = layoutType === 'desktop';

  const toggleStreamerMode = useCallback(() => {
    setIsStreamerMode((prev) => {
      const next = !prev;
      if (typeof window !== 'undefined') {
        try {
          localStorage.setItem(STREAMER_MODE_STORAGE_KEY, next ? 'true' : 'false');
        } catch {
          // Ignore write failures (e.g. privacy mode)
        }

        window.dispatchEvent(new CustomEvent(STREAMER_MODE_EVENT, { detail: next }));
      }
      return next;
    });
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const handleStreamerModeChange = (event: Event) => {
      const { detail } = event as CustomEvent<boolean>;
      setIsStreamerMode(detail);
    };

    const handleStorage = (event: StorageEvent) => {
      if (event.key === STREAMER_MODE_STORAGE_KEY) {
        setIsStreamerMode(event.newValue === 'true');
      }
    };

    window.addEventListener(STREAMER_MODE_EVENT, handleStreamerModeChange as EventListener);
    window.addEventListener('storage', handleStorage);

    return () => {
      window.removeEventListener(STREAMER_MODE_EVENT, handleStreamerModeChange as EventListener);
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  // Memoize the layout variant object to maintain reference equality
  const layoutVariant = useMemo<LayoutVariant>(() => ({
    type: isStreamerMode && isActuallyDesktop ? 'mobile' : layoutType,
    breakpoint: LAYOUT_BREAKPOINTS.desktop,
    isStreamerMode,
    toggleStreamerMode,
    isActuallyDesktop
  }), [layoutType, isStreamerMode, toggleStreamerMode, isActuallyDesktop]);

  // Optimized update function that only triggers when breakpoint actually changes
  // Note: This function is inlined in the useEffect to avoid dependency issues

  useEffect(() => {
    // Improved debouncing with longer delay to reduce update frequency
    let timeoutId: NodeJS.Timeout;
    let rafId: number;
    
    const handleResize = () => {
      // Cancel any pending updates
      clearTimeout(timeoutId);
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
      
      // Use requestAnimationFrame for better performance, then debounce
      rafId = requestAnimationFrame(() => {
        timeoutId = setTimeout(() => {
          // Inline the update logic to avoid dependency on updateVariant
          const currentWidth = window.innerWidth;
          const lastWidth = lastWidthRef.current;
          
          // Only proceed if width has changed significantly or crossed breakpoint
          if (Math.abs(currentWidth - lastWidth) >= 10) {
            lastWidthRef.current = currentWidth;
            
            const newType = currentWidth >= LAYOUT_BREAKPOINTS.desktop ? 'desktop' : 'mobile';
            const oldType = lastWidth >= LAYOUT_BREAKPOINTS.desktop ? 'desktop' : 'mobile';
            
            // Only update state if the breakpoint category has actually changed
            if (newType !== oldType) {
              setLayoutType(newType);
            }
          }
        }, 150); // Increased debounce to 150ms
      });
    };

    // Set initial variant on mount
    const currentWidth = window.innerWidth;
    lastWidthRef.current = currentWidth;
    const initialType = currentWidth >= LAYOUT_BREAKPOINTS.desktop ? 'desktop' : 'mobile';
    setLayoutType(initialType);

    // Create cleanup function for event listener
    const cleanupEventListener = createEventListenerCleanup(
      window,
      'resize',
      handleResize,
      { passive: true }
    );

    // Cleanup function for timeouts and animation frames
    const cleanupTimers = () => {
      clearTimeout(timeoutId);
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
    };

    // Combine all cleanup functions
    return combineCleanups(cleanupEventListener, cleanupTimers);
  }, []); // No dependencies needed - all logic is inlined

  return layoutVariant;
};
